import { BOOL } from "../../util/constants";
import { dropdownSubtypes, loggedinUserData, inputMappings } from "../../util/enums";

export const formSections = [
  {
    title: "Graduation Information",
    id: "graduation-information",
    questions: [
      {
        title: "Graduation Term",
        id: "graduation-information-term",
        required: true,
        type: inputMappings.dropDown,
        subtype: dropdownSubtypes.term,
        dataField: 'termCode',
        show: 3, // show this many options in the dropdown
        filter: {
          showHistorical: 1,
          showFromPresent: 3, // pull an additional option to use in the approval form
        }
      },
      {
        title: "Graduation Level",
        id: "graduation-information-level",
        required: true,
        type: inputMappings.dropDown,
        subtype: dropdownSubtypes.graduationLevel, 
        branch: "graduation-information-plan",
        dataField: 'gradLevel',
      },
      {
        title: "Graduation Plan",
        id: "graduation-information-plan",
        required: true,
        type: inputMappings.dropDown,
        subtype: dropdownSubtypes.graduatePrograms,
        filter: [
          {
            "field":  "graduation-information-level",
            "match": "gradLevel",
            operation: "includes"
          }
        ],
        branch: "committee-advisor",
        results: [{
          label: 'Degree',
          field: "degree",
          dataField: 'gradDegree'
        }],
        dependencyHelperText: "Graduation Level must be selected before choosing a Graduation Plan",
        dependencies: ["graduation-information-level"],
        dataField: 'academicPlan'
      },
      {
        //Summary to always show, clean up text at some point
        id: "end-of-semester",
        summary: 
          "The end of semester option you have selected means that you will not be graduating in the semester indicated on the form. The EOS option is to be used only if all degree requirements are to be completed by the posted end of semester deadline with the degree being conferred at the commencement ceremony of the following semester. Do not utilize this option if you are trying to graduate using the regular semester/summer term graduation deadlines. EOS is the only option available if the Application to Graduate for a given semester is submitted after the application deadline for that semester has passed.",
        type: inputMappings.checkbox,
        dataField: 'endOfSemesterOption',
        defaultValue: BOOL.NO,
        required: false,
        choices: [
          {
            id: "end-of-semester-yes",
            value: BOOL.YES,
            title: "End of Semester Option",
            branch: "end-of-semester-confirmation"
          }
        ]
      },
      {
        id: "end-of-semester-confirmation",
        title: "End of Semester Confirmation",
        type: inputMappings.checkbox,
        dataField: "endOfSemesterConfirmation",
        defaultValue: BOOL.NO,
        dependencies: ["end-of-semester"],
        required: true,
        summary: "In selecting the end of semester option for the semester indicated on this application I understand that I will be officially graduating in the following semester if all degree requirements are completed by the respective end of semester deadline.",
        choices: [
          {
            id: "end-of-semester-confirmation-yes",
            value: BOOL.YES,
            title: "Confirm"
          }
        ]
      }
    ]
  },
  {
    "id": "commencement",
    title: "Commencement",
    questions: [
      {
        id: "commencement-pick-up",
        type: inputMappings.radio,
        title: "Attendance",
        dataField: 'commencement',
        required: true,
        choices: [
          {
            id: "commencement-will-attend",
            title: "I will attend commencement",
            value: "ATTEND"
          },
          {
            id: "commencement-one-week",
            title:
              "I will not attend commencement, I will pick up my diploma within one week after commencement",
            value: "PICKUP"
          },
          {
            id: "commencement-authorized-pickup",
            title: "I will not attend commencement, I am authorizing someone else to pick up my diploma",
            value: "AUTHORIZED_PICKUP",
            branch: "commencement-authorized-pickup-persons",
          },
          {
            id: "commencement-authorized-pickup-persons",
            title: "Authorized Persons",
            dataField: 'authorizedPickupPeople',
            type: inputMappings.inputGroup,
            numberOfResponsesMinimum: 1,
            numberOfResponsesMaximum: 2,
            questions: [
              {
                id: "commencement-authorized-pickup-first-name",
                required: true,
                title: "First Name",
                type: inputMappings.textInput,
                dataField: 'firstName'
              },
              {
                id: "commencement-authorized-pickup-last-name",
                required: true,
                title: "Last Name",
                type: inputMappings.textInput,
                dataField: 'lastName'
              }
            ],
            dependencies: [ "commencement-pick-up" ]
          },
          {
            id: "commencement-mail",
            title: "I will not attend commencement, please mail my diploma to the permanent address below",
            value: "MAIL",
            branch: "commencement-mailing-address",
          },
          {
            id: "commencement-mailing-address",
            title: "Address",
            type: loggedinUserData.mailingAddress,
            dataField: 'mailAddress',
            dependencies: [ "commencement-pick-up" ],
          }
        ],
      },
    ],
    dependencies: [ "commencement-pick-up" ]
  },
  // createCommitteeSection()
  {
      title: "Committee",
      id: "committee",
      questions: [
        {
          id: "advisor-subheading",
          type: inputMappings.subheading,
          title: "Advisor Information"
        },
        {
          id: "committee-advisor",
          type: inputMappings.dropDown,
          dataField: "committee.advisor",
          title: "Advisor",
          summary: null,
          required: false,
          subtype: dropdownSubtypes.advisor,
          dependencies: ["graduation-information-plan"],
          dependencyHelperText: "Graduation Plan must be selected before choosing an Advisor"
        },
        {
          id: "co-advisor",
          type: inputMappings.dropDown,
          title: "Co-Advisor (if applicable)",
          subtype: dropdownSubtypes.coAdvisor,
          summary: null,
          required: false,
          dataField: "committee.coAdvisor"
        },
        {
          id: "committee-members",
          type: inputMappings.inputGroup,
          title: "Committee Members",
          subtitle: "Graduate Faculty committee members",
          summary: "(M)aster’s level faculty selection will require approval of the Graduate Studies Committee Chair.",
          required: false,
          numberOfResponsesMinimum: 0,
          numberOfResponsesMaximum: 10,
          dependencies: [],
          choices: [],
          dataField: "committee.members",
          questions: [
            {
              id: "committee-member-info",
              type: inputMappings.dropDown,
              subtype: dropdownSubtypes.facultyMember,
              title: "Faculty Member",
              summary: null,
              required: false
            }
          ]
        },
        {
          id: "external-members",
          type: inputMappings.inputGroup,
          title: "External Members",
          subtitle: "Non-Graduate Faculty committee members",
          summary: "For each external committee member, completion & submission of Committee and Examination Petition is required.",
          required: false,
          numberOfResponsesMinimum: 1,
          numberOfResponsesMaximum: 4,
          dependencies: [],
          choices: [],
          dataField: "committee.externalMembers",
          questions: [
            {
              id: "first-name",
              type: inputMappings.textInput,
              title: "First Name",
              dataField: 'firstName',
              summary: null,
              required: true,
              numberOfResponsesMinimum: 1,
              numberOfResponsesMaximum: 1,
              dependencies: [],
              choices: []
            },
            {
              id: "last-name",
              type: inputMappings.textInput,
              title: "Last Name",
              summary: null,
              required: true,
              dataField: 'lastName',
              numberOfResponsesMinimum: 1,
              numberOfResponsesMaximum: 1,
              dependencies: [],
              choices: []
            },
            {
              id: "email",
              type: inputMappings.email,
              title: "Email Address",
              dataField: "email",
              summary: null,
              required: true,
              numberOfResponsesMinimum: 1,
              numberOfResponsesMaximum: 1,
              dependencies: [],
              choices: []
            }
          ]
        }
      ]
    }
];

export const studentQuestions = [
  {
    id: "commencement-pick-up",
    type: inputMappings.radio,
    title: "Commencement Attendance",
    dataField: 'commencement',
    choices: [
      {
        id: "commencement-will-attend",
        title: "I will attend commencement",
        value: "ATTEND"
      },
      {
        id: "commencement-one-week",
        title:
          "I will not attend commencement, I will pick up my diploma within one week after commencement",
        value: "PICKUP"
      },
      {
        id: "commencement-authorized-pickup",
        title: "I will not attend commencement, I am authorizing someone else to pick up my diploma",
        value: "AUTHORIZED_PICKUP",
        branch: "commencement-authorized-pickup-persons",
      },
      {
        id: "commencement-authorized-pickup-persons",
        title: "Authorized Persons",
        dataField: 'authorizedPickupPeople',
        type: inputMappings.inputGroup,
        numberOfResponsesMinimum: 1,
        numberOfResponsesMaximum: 2,
        questions: [
          {
            id: "commencement-authorized-pickup-first-name",
            required: true,
            title: "First Name",
            type: inputMappings.textInput,
            dataField: 'firstName'
          },
          {
            id: "commencement-authorized-pickup-last-name",
            required: true,
            title: "Last Name",
            type: inputMappings.textInput,
            dataField: 'lastName'
          }
        ],
        dependencies: [ "commencement-pick-up" ]
      },
      {
        id: "commencement-mail",
        title: "I will not attend commencement, please mail my diploma to the permanent address below",
        value: "MAIL",
        branch: "commencement-mailing-address",
      },
      {
        id: "commencement-mailing-address",
        title: "Address",
        type: loggedinUserData.mailingAddress,
        dataField: 'mailAddress',
        dependencies: [ "commencement-pick-up" ],
      }
    ]
  }
];

export const additionalFilters = [ {
  title: "Graduation Level",
  id: "level",
  required: true,
  type: inputMappings.dropDown,
  subtype: dropdownSubtypes.graduationLevel, 
  dataField: 'gradLevel',
},
{
  title: "Graduation Degree",
  id: "degree",
  required: true,
  type: inputMappings.dropDown,
  subtype: dropdownSubtypes.degrees,
  dataField: 'degree'
},
{
  id: "eos-option",
  title: "End of Semester Option",
  required: false,
  type: inputMappings.dropDown,
  choices: [{
    id: "all-eos",
    title: "All",
    value: "ALL"
  },
  {
    id: "yes-eos",
    title: "Yes",
    value: "Y"
  },
  {
    id: "no-eos",
    title: "No",
    value: "N"
  }
  ]
}];